import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut } from '@appic/ui-components'
import * as React from "react"
import { globalActionRegistry, type RegisteredAction, globalEntitySearch, type EntityResult } from "@appic/command-palette"

export interface CommandPaletteProps {
  isOpen: boolean
  onOpenChange: (v: boolean) => void
  ctx?: { entity?: string; selection?: unknown; route?: string }
}

export function CommandPalette({ isOpen, onOpenChange, ctx }: CommandPaletteProps) {
  const [query, setQuery] = React.useState('')
  const [actions, setActions] = React.useState<RegisteredAction[]>([])
  const [entityResults, setEntityResults] = React.useState<EntityResult[]>([])

  React.useEffect(() => {
    setActions(globalActionRegistry.list())
    const off = globalActionRegistry.on(() => setActions(globalActionRegistry.list()))
    return off
  }, [])

  // Debounced entity search
  React.useEffect(() => {
    if (!query.trim()) {
      setEntityResults([])
      return
    }
    const timer = setTimeout(async () => {
      const results = await globalEntitySearch.search(query)
      setEntityResults(results)
    }, 200)
    return () => clearTimeout(timer)
  }, [query])

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        onOpenChange(!isOpen)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onOpenChange])

  // Filter actions by query
  const filteredActions = React.useMemo(() => {
    if (!query) return actions
    const q = query.toLowerCase()
    return actions.filter(a =>
      a.label.toLowerCase().includes(q) ||
      a.keywords?.some(k => k.toLowerCase().includes(q))
    )
  }, [actions, query])

  // Group entities by module
  const entityByModule = React.useMemo(() => {
    const groups: Record<string, EntityResult[]> = {}
    for (const e of entityResults) {
      const mod = e.module || 'Results'
      if (!groups[mod]) groups[mod] = []
      groups[mod].push(e)
    }
    return groups
  }, [entityResults])

  return (
    <CommandDialog open={isOpen} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Type a command or search..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {/* Entity results — shown first when searching */}
        {Object.entries(entityByModule).map(([module, entities]) => (
          <CommandGroup key={`entity-${module}`} heading={module}>
            {entities.map((entity) => (
              <CommandItem
                key={entity.id}
                value={entity.label.toLowerCase()}
                onSelect={() => {
                  entity.onSelect?.()
                  onOpenChange(false)
                }}
              >
                <div className="flex flex-col">
                  <span>{entity.label}</span>
                  {entity.sublabel && (
                    <span className="text-xs text-muted-foreground">{entity.sublabel}</span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}

        {entityResults.length > 0 && filteredActions.length > 0 && (
          <CommandSeparator />
        )}

        {/* Actions */}
        {filteredActions.length > 0 && (
          <CommandGroup heading="Actions">
            {filteredActions.map((a) => (
              <CommandItem
                key={a.id}
                value={`${a.label} ${a.keywords?.join(' ')}`}
                onSelect={() => {
                  globalActionRegistry.run(a.id, ctx)
                  onOpenChange(false)
                }}
              >
                <span>{a.label}</span>
                {a.shortcut && <CommandShortcut>{a.shortcut}</CommandShortcut>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  )
}
